import React from "react";
import { Container, Row, Col, Card, Button, Badge } from "react-bootstrap";
import {
  FaBuilding,
  FaIndustry,
  FaStore,
  FaHospital,
  FaSchool,
  FaHotel,
  FaThermometerHalf,
  FaCog,
  FaShieldAlt,
  FaCheckCircle,
  FaWrench,
  FaClock,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";

const Commercial = () => {
  const commercialServices = [
    {
      icon: <FaBuilding className="icon-large" />,
      title: "Office Buildings",
      description:
        "Complete HVAC solutions for multi-story office complexes and corporate headquarters.",
      features: [
        "Zoned climate control",
        "Energy management",
        "24/7 monitoring",
        "Smart building integration",
      ],
    },
    {
      icon: <FaStore className="icon-large" />,
      title: "Retail Spaces",
      description:
        "Climate control systems designed for shopping centers, malls, and retail stores.",
      features: [
        "Customer comfort",
        "Energy efficiency",
        "Flexible zoning",
        "Maintenance programs",
      ],
    },
    {
      icon: <FaIndustry className="icon-large" />,
      title: "Industrial Facilities",
      description:
        "Heavy-duty HVAC systems for manufacturing plants and industrial complexes.",
      features: [
        "High-capacity cooling",
        "Process cooling",
        "Dust filtration",
        "Temperature control",
      ],
    },
    {
      icon: <FaHospital className="icon-large" />,
      title: "Healthcare Facilities",
      description:
        "Specialized HVAC systems for hospitals, clinics, and medical centers.",
      features: [
        "Air quality control",
        "Infection prevention",
        "Precise temperature",
        "Compliance standards",
      ],
    },
    {
      icon: <FaSchool className="icon-large" />,
      title: "Educational Institutions",
      description:
        "Reliable climate control for schools, universities, and educational facilities.",
      features: [
        "Student comfort",
        "Energy savings",
        "Smart scheduling",
        "Maintenance support",
      ],
    },
    {
      icon: <FaHotel className="icon-large" />,
      title: "Hospitality & Hotels",
      description:
        "Guest comfort systems for hotels, resorts, and hospitality venues.",
      features: [
        "Guest satisfaction",
        "Energy optimization",
        "Quiet operation",
        "24/7 reliability",
      ],
    },
  ];

  const benefits = [
    "Reduced energy costs by up to 30%",
    "Improved indoor air quality",
    "Enhanced employee productivity",
    "Compliance with building codes",
    "Extended equipment lifespan",
    "24/7 emergency support",
  ];

  return (
    <>
      <Helmet>
        <title>
          Commercial AC Service Lahore | Office, Retail & Industrial HVAC
        </title>
        <meta
          name="description"
          content="Expert commercial AC service in Lahore & Punjab. HVAC solutions for offices, retail, industry, hospitals, and hotels. 24/7 support. کمرشل ای سی سروس لاہور۔"
        />
        <meta
          name="keywords"
          content="Commercial AC Service Lahore, Office HVAC Lahore, Industrial AC Lahore, Retail AC Service, کمرشل ای سی سروس لاہور"
        />
        <meta
          property="og:title"
          content="Commercial AC Service Lahore | Office, Retail & Industrial HVAC"
        />
        <meta
          property="og:description"
          content="Expert commercial AC service in Lahore & Punjab. HVAC solutions for offices, retail, industry, hospitals, and hotels. 24/7 support."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://procoolac.com/commercial" />
        <meta
          property="og:image"
          content="https://procoolac.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Commercial AC Service Lahore | Office, Retail & Industrial HVAC"
        />
        <meta
          name="twitter:description"
          content="Expert commercial AC service in Lahore & Punjab. HVAC solutions for offices, retail, industry, hospitals, and hotels. 24/7 support."
        />
        <meta
          name="twitter:image"
          content="https://procoolac.com/og-image.jpg"
        />
      </Helmet>
      <section className="section-padding bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <Badge bg="primary" className="mb-3 px-3 py-2 fs-6">
                Commercial Solutions
              </Badge>
              <h2 className="display-5 fw-bold mb-3 text-gradient">
                Commercial HVAC Excellence
              </h2>
              <p className="lead text-muted">
                Comprehensive air conditioning solutions for businesses of all
                sizes. From office buildings to industrial facilities, we
                deliver reliable, energy-efficient climate control systems that
                keep your operations running smoothly.
              </p>
            </Col>
          </Row>

          {/* Commercial Services Grid */}
          <Row className="g-4 mb-5">
            {commercialServices.map((service, index) => (
              <Col lg={4} md={6} key={index}>
                <Card className="h-100 border-0 shadow-sm card-hover">
                  <Card.Body className="p-4">
                    <div className="text-center mb-3">{service.icon}</div>
                    <h4 className="fw-bold mb-3 text-center">
                      {service.title}
                    </h4>
                    <p className="text-muted mb-4 text-center">
                      {service.description}
                    </p>

                    <ul className="list-unstyled">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="mb-2">
                          <FaCheckCircle className="text-success me-2" />
                          <small>{feature}</small>
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Benefits Section */}
          <Row className="mb-5">
            <Col lg={6} className="mb-4">
              <div className="bg-primary text-white rounded-4 p-5 h-100">
                <h3 className="fw-bold mb-4">Commercial Benefits</h3>
                <div className="row g-3">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="col-6">
                      <div className="d-flex align-items-center">
                        <FaCheckCircle className="text-warning me-2" />
                        <span className="small">{benefit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            <Col lg={6} className="mb-4">
              <div className="bg-white border rounded-4 p-5 h-100 shadow-sm">
                <h3 className="fw-bold mb-4 text-primary">
                  Why Choose Our Commercial Services?
                </h3>

                <div className="d-flex align-items-start mb-3">
                  <FaThermometerHalf className="text-primary me-3 mt-1" />
                  <div>
                    <h6 className="fw-bold mb-1">Expert Installation</h6>
                    <p className="small text-muted mb-0">
                      Professional installation by certified technicians
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start mb-3">
                  <FaCog className="text-primary me-3 mt-1" />
                  <div>
                    <h6 className="fw-bold mb-1">Custom Solutions</h6>
                    <p className="small text-muted mb-0">
                      Tailored HVAC systems for your specific needs
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start mb-3">
                  <FaShieldAlt className="text-primary me-3 mt-1" />
                  <div>
                    <h6 className="fw-bold mb-1">Warranty Protection</h6>
                    <p className="small text-muted mb-0">
                      Comprehensive warranty on all installations
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start">
                  <FaClock className="text-primary me-3 mt-1" />
                  <div>
                    <h6 className="fw-bold mb-1">24/7 Support</h6>
                    <p className="small text-muted mb-0">
                      Round-the-clock emergency service available
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Call to Action */}
          <Row>
            <Col className="text-center">
              <div className="gradient-bg text-white rounded-4 p-5">
                <h3 className="fw-bold mb-3">
                  Ready to Upgrade Your Commercial HVAC?
                </h3>
                <p className="lead mb-4">
                  Get a free consultation and quote for your commercial air
                  conditioning needs.
                </p>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  <Button
                    variant="warning"
                    size="lg"
                    className="fw-semibold px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    <FaWrench className="me-2" />
                    Get Commercial Quote
                  </Button>
                  <Button
                    variant="outline-light"
                    size="lg"
                    className="fw-semibold px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    <FaClock className="me-2" />
                    Schedule Consultation
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Commercial;
